import { isLobbyCode } from '../../shared/lobbyCode.ts';

const KEY = 'pending-invite';

/**
 * Survives the URL being cleaned up.
 *
 * `/j/CODE` is rewritten to `/` the moment it is read, so the code is gone from
 * the address bar while the guest account is still being created. Without this
 * stash, a reload in that window lands the viewer in an empty lobby having done
 * everything right, which on a live stream reads as "the site is broken".
 *
 * `sessionStorage`, not `localStorage`: an invite is scoped to this tab and this
 * sitting. A code left behind for a week would silently drop someone into a
 * stranger's game.
 */
export function rememberInvite(code: string): void {
  try {
    sessionStorage.setItem(KEY, code);
  } catch {
    // Private browsing or a storage-blocking extension. The signed-in path still
    // works; only the sign-in-first case loses its code.
  }
}

/**
 * Reads without consuming.
 *
 * Read and clear are deliberately separate. The code has to survive from the
 * scan until the join is actually sent, which is a different moment from when
 * it is read on first render — clearing at read time would drop the code
 * precisely in the case this module exists for.
 */
export function readInvite(): string | null {
  try {
    const code = sessionStorage.getItem(KEY);
    return code !== null && isLobbyCode(code) ? code : null;
  } catch {
    return null;
  }
}

/** Called once the invite has actually been sent. An invite is single use. */
export function clearInvite(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do: if storage is unavailable, nothing was stored.
  }
}
